import { NgFor, NgIf, NgTemplateOutlet } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Button } from "@ankaadia/ankaadia-shared-frontend";

export interface MenuItemCommandEvent<TData = any> {
  /**
   * Original mouse event.
   */
  originalEvent: MouseEvent;

  /**
   * The menu item that was clicked.
   */
  item: SimpleMenuBarItem<TData>;

  /**
   * Index of the selected item.
   */
  index: number;
}

export interface SimpleMenuBarItem<TData = any> {
  /**
   * Text of the item.
   */
  label: string;

  /**
   * Identifier of the item.
   */
  id: string;

  /**
   * Value of the badge.
   */
  badge?: string;

  data: TData;
}

@Component({
  selector: "app-simple-menubar",
  templateUrl: "./simple-menu-bar.component.html",
  styleUrl: "./simple-menu-bar.component.scss",
  imports: [NgIf, NgFor, NgTemplateOutlet, Button],
  host: {
    "(document:click)": "onDocumentClick($event)",
  },
})
export class SimpleMenubarComponent implements AfterViewInit, OnDestroy {
  private resizeObserver: ResizeObserver | null = null;

  protected isMobile = false;
  protected isOpen = false;

  @Input() items: SimpleMenuBarItem[] = [];
  @Input() selectedItem: SimpleMenuBarItem | null = null;
  @Output() readonly selectedItemChange = new EventEmitter<SimpleMenuBarItem | null>();

  @ViewChild("nav") nav: ElementRef<HTMLElement> | undefined;
  @ViewChild("menu") menu: ElementRef<HTMLElement> | undefined;

  @ContentChild("itemTemplate") itemTemplate: TemplateRef<any> | undefined;
  @ContentChild("selectedItemTemplate") selectedItemTemplate: TemplateRef<any> | undefined;
  @ContentChild("endTemplate") endTemplate: TemplateRef<any> | undefined;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.nav?.nativeElement && this.menu?.nativeElement) {
        this.resizeObserver = new ResizeObserver(() => this.checkOverflow());
        this.resizeObserver.observe(this.nav.nativeElement);
        this.checkOverflow();
      }
    }, 100);
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen || !this.nav?.nativeElement) return;

    const clickedInside = this.nav.nativeElement.contains(event.target as Node);
    if (!clickedInside) {
      this.isOpen = false;
      this.cdr.detectChanges();
    }
  }

  protected toggleMenu(): void {
    this.isOpen = !this.isOpen;
  }

  protected closeMenu(): void {
    this.isOpen = false;
  }

  protected selectItem(event: MenuItemCommandEvent): void {
    this.selectedItemChange.emit(event.item);
  }

  private checkOverflow(): void {
    if (!this.menu?.nativeElement || !this.nav?.nativeElement) return;

    const menuWidth = this.menu.nativeElement.scrollWidth;
    const navWidth = this.nav.nativeElement.clientWidth;

    const wasMobile = this.isMobile;
    this.isMobile = menuWidth > navWidth;
    if (wasMobile && !this.isMobile) {
      this.isOpen = false;
    }

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }
}
